import ApplicationController from "./application_controller"
import Logger from '../lib/logger'
import { showToast } from '../utils/toast'
import { validatePricing, parsePricingValue, formatPricing } from '../utils/pricing'

export default class extends ApplicationController {
  static targets = ["providerName", "providerField", "nrcField", "mrcField"]

  connect() {
    super.connect()
    this.updateCardHeader()
  }

  updateProviderName() {
    const providerName = this.providerFieldTarget.value.trim()
    if (this.hasProviderNameTarget) {
      this.providerNameTarget.textContent = providerName || "New Provider Response"
    }
    this.updateCardHeader()
  }

  updatePricing() {
    this.updateCardHeader()
    this.validatePricingFields()
  }

  updateCardHeader() {
    const card = this.element.querySelector('.card')
    const quoteSummary = card?.querySelector('.quote-summary')

    if (!quoteSummary) return

    const nrc = parsePricingValue(this.hasNrcFieldTarget ? this.nrcFieldTarget.value : 0)
    const mrc = parsePricingValue(this.hasMrcFieldTarget ? this.mrcFieldTarget.value : 0)
    const providerName = this.hasProviderFieldTarget ? this.providerFieldTarget.value.trim() : ""

    let badgesHtml = ""

    if (nrc > 0) {
      badgesHtml += `<span class="badge bg-success me-1">
        <i class="fas fa-dollar-sign me-1"></i>$${formatPricing(nrc)} NRC
      </span>`
    }

    if (mrc > 0) {
      badgesHtml += `<span class="badge bg-primary">
        <i class="fas fa-calendar me-1"></i>$${formatPricing(mrc)}/mo
      </span>`
    }

    if (badgesHtml === "") {
      if (providerName) {
        badgesHtml = `<span class="badge bg-warning text-dark">
          <i class="fas fa-edit me-1"></i>Needs Pricing
        </span>`
      } else {
        badgesHtml = `<span class="badge bg-secondary">
          <i class="fas fa-edit me-1"></i>Incomplete
        </span>`
      }
    }

    quoteSummary.innerHTML = `<div class="price-display">${badgesHtml}</div>`
    this.updateCompletionStatus(nrc, mrc, providerName)
  }

  updateCompletionStatus(nrc, mrc, providerName) {
    const card = this.element.querySelector('.card')
    if (!card) return

    const isComplete = providerName && (nrc > 0 || mrc > 0)

    card.classList.remove('quote-complete', 'quote-incomplete', 'quote-partial')

    if (isComplete) {
      card.classList.add('quote-complete')
    } else if (providerName || nrc > 0 || mrc > 0) {
      card.classList.add('quote-partial')
    } else {
      card.classList.add('quote-incomplete')
    }
  }

  validatePricingFields() {
    const nrc = parsePricingValue(this.hasNrcFieldTarget ? this.nrcFieldTarget.value : 0)
    const mrc = parsePricingValue(this.hasMrcFieldTarget ? this.mrcFieldTarget.value : 0)

    const warnings = validatePricing(nrc, mrc)

    if (warnings.length > 0) {
      this.showValidationWarnings(warnings)
    } else {
      this.clearValidationWarnings()
    }
  }

  showValidationWarnings(warnings) {
    let warningDiv = this.element.querySelector('.pricing-warnings')

    if (!warningDiv) {
      warningDiv = document.createElement('div')
      warningDiv.className = 'pricing-warnings alert alert-warning alert-dismissible fade show mt-2'
      warningDiv.innerHTML = `
        <i class="fas fa-exclamation-triangle me-2"></i>
        <strong>Pricing Warnings:</strong>
        <ul class="mb-0 mt-1"></ul>
        <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
      `
      const cardBody = this.element.querySelector('.card-body')
      if (cardBody) {
        cardBody.appendChild(warningDiv)
      }
    }

    const warningList = warningDiv?.querySelector('ul')
    if (warningList) {
      // SECURITY: HTML-escape warnings to prevent XSS via innerHTML
      const escapeHtml = (str) => str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      warningList.innerHTML = warnings.map(warning => `<li>${escapeHtml(warning)}</li>`).join('')
    }
  }

  clearValidationWarnings() {
    const warningDiv = this.element.querySelector('.pricing-warnings')
    if (warningDiv) {
      warningDiv.remove()
    }
  }

  duplicateQuote() {
    const formData = this.getFormData()

    const nestedFormElement = document.querySelector('[data-controller*="nested-form"]')
    const nestedFormController = nestedFormElement && this.application.getControllerForElementAndIdentifier(
      nestedFormElement,
      'nested-form'
    )

    // NestedFormController has no add() method — new fields are added entirely by a server
    // round-trip (see docs/NESTED_FORMS_MIGRATION_GUIDE.md §"Adding Fields"): the real "Add"
    // button is a Turbo Stream link, not a client-side template clone, so there's no
    // synchronous/Promise hook here to call populateNewForm(formData) after. Trigger the same
    // button a user would click; populateNewForm is a no-op stub today regardless (see below),
    // so nothing is lost by not chaining it here.
    const addButton = nestedFormElement && nestedFormElement.querySelector('[data-nested-form-target="addButton"]')
    if (addButton) {
      addButton.click()
    } else if (nestedFormController) {
      Logger.warn('duplicateQuote: no add button found for the nested-form controller', { formData })
    } else {
      Logger.warn('duplicateQuote: no nested-form controller found on the page', { formData })
    }
  }

  validateQuote() {
    const errors = this.performValidation()

    if (errors.length === 0) {
      showToast('Quote validation passed!', 'success')

      const card = this.element.querySelector('.card')
      if (card) {
        card.classList.add('validation-success')
        setTimeout(() => {
          card.classList.remove('validation-success')
        }, 2000)
      }
    } else {
      showToast(`Validation failed: ${errors.join(', ')}`, 'error')
    }
  }

  performValidation() {
    const providerName = this.hasProviderFieldTarget ? this.providerFieldTarget.value.trim() : ""
    const nrc = parsePricingValue(this.hasNrcFieldTarget ? this.nrcFieldTarget.value : 0)
    const mrc = parsePricingValue(this.hasMrcFieldTarget ? this.mrcFieldTarget.value : 0)

    const errors = []

    if (!providerName) {
      errors.push('Provider name is required')
    }

    if (nrc === 0 && mrc === 0) {
      errors.push('At least one pricing field (NRC or MRC) must be greater than zero')
    }

    return errors
  }

  getFormData() {
    return {
      providername: this.hasProviderFieldTarget ? this.providerFieldTarget.value : "",
      nrc: this.hasNrcFieldTarget ? this.nrcFieldTarget.value : "",
      mrc: this.hasMrcFieldTarget ? this.mrcFieldTarget.value : ""
    }
  }

  populateNewForm(data) {
    Logger.debug('Populating new form with data:', data)
  }
}
